import { useMutation, useQueryClient } from '@tanstack/react-query'

import { API_URL } from '@/constants/env'

import { useHandleError } from '@/hooks/common/useErrorHandler'
import {
  pursuitBookmarkCheckQueryKeys,
  pursuitBookmarkIdsQueryKeys,
} from '@/hooks/pursuitBookmarks/types'
import type { BookmarkedPursuitIdsResponse } from '@/hooks/pursuitBookmarks/useBookmarkedPursuitIds'
import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'

import type { PursuitBookmarkCheck } from '@/types/bookmark/PursuitBookmark'
import type { UUID } from '@/types/Id'

interface CreatePursuitBookmarkArgs {
  orgId: UUID
  pursuitId: UUID
}

const createPursuitBookmark = async (
  orgId: UUID,
  pursuitId: UUID,
  accessToken: string | undefined | null,
): Promise<void> => {
  if (accessToken === null || accessToken === undefined) {
    throw new Error('No access token provided.')
  }

  const res = await fetch(
    `${API_URL}/api/v1/organizations/${orgId}/pursuits/${pursuitId}/bookmark`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    },
  )

  if (!res.ok) {
    throw new Error(res.statusText)
  }
}

interface MutationContext {
  previousIds: BookmarkedPursuitIdsResponse | undefined
  previousCheck: PursuitBookmarkCheck | undefined
}

export const useCreatePursuitBookmark = () => {
  const { accessToken } = useAuthInfoWithThrow()
  const { handleError } = useHandleError()
  const queryClient = useQueryClient()

  return useMutation<void, Error, CreatePursuitBookmarkArgs, MutationContext>({
    mutationFn: ({ orgId, pursuitId }) =>
      createPursuitBookmark(orgId, pursuitId, accessToken),

    onMutate: async ({ orgId, pursuitId }) => {
      // Cancel any outgoing refetches so they don't overwrite our optimistic update
      const idsQueryKey = pursuitBookmarkIdsQueryKeys.organization({ orgId })
      const checkKey = pursuitBookmarkCheckQueryKeys.check({ orgId, pursuitId })

      await queryClient.cancelQueries({ queryKey: idsQueryKey })
      await queryClient.cancelQueries({ queryKey: checkKey })

      // Snapshot previous values
      const previousIds =
        queryClient.getQueryData<BookmarkedPursuitIdsResponse>(idsQueryKey)
      const previousCheck =
        queryClient.getQueryData<PursuitBookmarkCheck>(checkKey)

      // Optimistically add the pursuit ID
      if (previousIds) {
        queryClient.setQueryData<BookmarkedPursuitIdsResponse>(idsQueryKey, {
          pursuitIds: [pursuitId, ...previousIds.pursuitIds],
          count: previousIds.count + 1,
        })
      }

      // Optimistically update the check cache
      const checkData: PursuitBookmarkCheck = {
        is_bookmarked: true,
        pursuit_id: pursuitId,
      }
      queryClient.setQueryData(checkKey, checkData)

      return { previousIds, previousCheck }
    },

    onError: (err, { orgId, pursuitId }, context) => {
      // Rollback IDs on error
      if (context?.previousIds) {
        const idsQueryKey = pursuitBookmarkIdsQueryKeys.organization({ orgId })
        queryClient.setQueryData(idsQueryKey, context.previousIds)
      }

      // Rollback check cache on error
      const checkKey = pursuitBookmarkCheckQueryKeys.check({ orgId, pursuitId })
      if (context?.previousCheck) {
        queryClient.setQueryData(checkKey, context.previousCheck)
      } else {
        // No previous data means it wasn't bookmarked before
        queryClient.setQueryData<PursuitBookmarkCheck>(checkKey, {
          is_bookmarked: false,
          pursuit_id: null,
        })
      }

      handleError(err)
    },

    onSettled: (_, __, { orgId }) => {
      // Refetch to ensure consistency
      return queryClient.invalidateQueries({
        queryKey: pursuitBookmarkIdsQueryKeys.organization({ orgId }),
      })
    },
  })
}
